import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Award,
  BicepsFlexed,
  Flame,
  Heart,
  HeartHandshake,
  Laugh,
  MessageCircle,
  Paperclip,
  PartyPopper,
  Pin,
  Sparkles,
  Star,
  ThumbsUp,
  X,
} from "lucide-react";
import { api } from "../api/client";
import BottomNav from "../components/BottomNav";
import { useFeatures } from "../context/FeaturesContext";
import useFilterParams from "../hooks/useFilterParams";
import type {
  CompanyValue,
  SocialPost,
  SocialComment,
} from "../types";
import { EmployeeSearchMulti } from "../components/EmployeeSearch";

const REACTION_ICON_PROPS = { size: 16, strokeWidth: 1.75, "aria-hidden": true } as const;

// Reactions are drawn as line icons, not emoji, to match the rest of the app.
const REACTION_ICONS: Record<string, ReactNode> = {
  clap: <Award {...REACTION_ICON_PROPS} />,
  heart: <Heart {...REACTION_ICON_PROPS} />,
  fire: <Flame {...REACTION_ICON_PROPS} />,
  star: <Star {...REACTION_ICON_PROPS} />,
  thumbsup: <ThumbsUp {...REACTION_ICON_PROPS} />,
  laugh: <Laugh {...REACTION_ICON_PROPS} />,
  surprised: <Sparkles {...REACTION_ICON_PROPS} />,
  celebrate: <PartyPopper {...REACTION_ICON_PROPS} />,
  strong: <BicepsFlexed {...REACTION_ICON_PROPS} />,
  thankyou: <HeartHandshake {...REACTION_ICON_PROPS} />,
};

const REACTION_LABELS: Record<string, string> = {
  clap: "Well done",
  heart: "Love",
  fire: "On fire",
  star: "Star",
  thumbsup: "Like",
  laugh: "Funny",
  surprised: "Wow",
  celebrate: "Celebrate",
  strong: "Strong",
  thankyou: "Thank you",
};

const POST_TYPE_STYLES: Record<string, string> = {
  recognition: "bg-green-50 text-green-700",
  announcement: "bg-blue-50 text-blue-700",
  birthday: "bg-yellow-50 text-yellow-700",
  anniversary: "bg-purple-50 text-purple-700",
  welcome: "bg-teal-50 text-teal-700",
};

type FilterType = "all" | "recognition" | "announcement" | "celebrations";

const FILTERS: { key: FilterType; label: string }[] = [
  { key: "all", label: "All" },
  { key: "recognition", label: "Recognition" },
  { key: "announcement", label: "Announcements" },
  { key: "celebrations", label: "Celebrations" },
];

interface FeedResponse {
  items: SocialPost[];
  total: number;
  page: number;
  per_page: number;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function formatPostType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function isImageType(ct: string): boolean {
  return ct.startsWith("image/");
}

// ---------------------------------------------------------------------------
// Comment reaction bar
// ---------------------------------------------------------------------------

function CommentReactionBar({
  commentId,
  reactions,
  onReact,
}: {
  commentId: number;
  reactions: { reaction_type: string; count: number; reacted: boolean }[];
  onReact: (commentId: number, type: string) => void;
}) {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showPicker) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node))
        setShowPicker(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showPicker]);

  const activeReactions = reactions.filter((r) => r.count > 0);

  return (
    <div className="mt-1 flex items-center gap-1">
      {activeReactions.map((r) => (
        <button
          key={r.reaction_type}
          type="button"
          onClick={() => onReact(commentId, r.reaction_type)}
          className={[
            "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs transition-colors",
            r.reacted
              ? "bg-blue-50 ring-1 ring-blue-200"
              : "bg-gray-50 hover:bg-gray-100",
          ].join(" ")}
        >
          <span>{REACTION_ICONS[r.reaction_type] ?? r.reaction_type}</span>
          <span className="text-gray-600">{r.count}</span>
        </button>
      ))}
      <div className="relative" ref={pickerRef}>
        <button
          type="button"
          onClick={() => setShowPicker((o) => !o)}
          className="rounded-full px-1.5 py-0.5 text-xs text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          title="React"
        >
          +
        </button>
        {showPicker && (
          <div className="absolute bottom-full left-0 z-20 mb-1 flex gap-0.5 rounded-lg border border-gray-200 bg-white p-1 shadow-lg">
            {Object.entries(REACTION_ICONS).map(([type, icon]) => (
              <button
                key={type}
                type="button"
                onClick={() => {
                  onReact(commentId, type);
                  setShowPicker(false);
                }}
                className="rounded p-1 text-sm hover:bg-gray-100"
                title={REACTION_LABELS[type] ?? type}
                aria-label={REACTION_LABELS[type] ?? type}
              >
                {icon}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comments section
// ---------------------------------------------------------------------------

function CommentsSection({
  postId,
  commentCount,
  recognitionId,
}: {
  postId: number;
  commentCount: number;
  recognitionId: number | null;
}) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState<SocialComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localCount, setLocalCount] = useState(commentCount);

  const loadComments = useCallback(async () => {
    setLoading(true);
    try {
      if (recognitionId) {
        const data = await api.get<
          { id: number; recognition_id: number; employee_id: number; employee_name: string; comment: string; reactions: { reaction_type: string; count: number; reacted: boolean }[]; created_at: string }[]
        >(`/api/recognition/${recognitionId}/comments`);
        setComments(
          data.map((c) => ({
            id: c.id,
            social_post_id: postId,
            employee_id: c.employee_id,
            employee_name: c.employee_name,
            comment: c.comment,
            reactions: c.reactions,
            created_at: c.created_at,
          })),
        );
        setLocalCount(data.length);
      } else {
        const data = await api.get<SocialComment[]>(
          `/api/social/${postId}/comments`,
        );
        setComments(data);
        setLocalCount(data.length);
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [postId, recognitionId]);

  const handleToggle = () => {
    if (!open) void loadComments();
    setOpen((o) => !o);
  };

  const handleSubmit = async () => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    try {
      if (recognitionId) {
        await api.post(`/api/recognition/${recognitionId}/comment`, {
          comment: text.trim(),
        });
      } else {
        await api.post(`/api/social/${postId}/comment`, {
          comment: text.trim(),
        });
      }
      setText("");
      await loadComments();
    } catch {
      /* ignore */
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommentReact = async (commentId: number, reactionType: string) => {
    if (!recognitionId) return;
    try {
      const resp = await api.post<{ action: string; reaction_type: string; old_reaction_type?: string }>(
        `/api/recognition/comments/${commentId}/react`,
        { reaction_type: reactionType },
      );
      setComments((prev) =>
        prev.map((c) => {
          if (c.id !== commentId) return c;
          const reactions = c.reactions ?? [];
          if (resp.action === "removed") {
            return {
              ...c,
              reactions: reactions
                .map((r) =>
                  r.reaction_type === reactionType
                    ? { ...r, count: r.count - 1, reacted: false }
                    : r,
                )
                .filter((r) => r.count > 0),
            };
          }
          if (resp.action === "replaced" && resp.old_reaction_type) {
            let updated = reactions.map((r) => {
              if (r.reaction_type === resp.old_reaction_type)
                return { ...r, count: r.count - 1, reacted: false };
              if (r.reaction_type === reactionType)
                return { ...r, count: r.count + 1, reacted: true };
              return r;
            }).filter((r) => r.count > 0);
            if (!updated.some((r) => r.reaction_type === reactionType)) {
              updated = [...updated, { reaction_type: reactionType, count: 1, reacted: true }];
            }
            return { ...c, reactions: updated };
          }
          // added
          const existing = reactions.find((r) => r.reaction_type === reactionType);
          if (existing) {
            return {
              ...c,
              reactions: reactions.map((r) =>
                r.reaction_type === reactionType
                  ? { ...r, count: r.count + 1, reacted: true }
                  : r,
              ),
            };
          }
          return {
            ...c,
            reactions: [
              ...reactions,
              { reaction_type: reactionType, count: 1, reacted: true },
            ],
          };
        }),
      );
    } catch {
      /* ignore */
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleToggle}
        className="text-sm text-gray-500 hover:text-gray-700"
      >
        <MessageCircle size={15} strokeWidth={1.75} aria-hidden className="mr-1 inline-block align-[-2px]" />{localCount} comment{localCount !== 1 ? "s" : ""}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          {loading && (
            <p className="text-xs text-gray-400">Loading comments...</p>
          )}
          {!loading && comments.length === 0 && (
            <p className="text-xs text-gray-400">No comments yet.</p>
          )}
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2">
              <div className="flex-1">
                <p className="text-sm">
                  <span className="font-semibold text-gray-900">
                    {c.employee_name}
                  </span>{" "}
                  <span className="text-gray-600">{c.comment}</span>
                </p>
                <p className="text-xs text-gray-400">
                  {relativeTime(c.created_at)}
                </p>
                {recognitionId && (
                  <CommentReactionBar
                    commentId={c.id}
                    reactions={c.reactions ?? []}
                    onReact={handleCommentReact}
                  />
                )}
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSubmit();
              }}
              placeholder="Write a comment..."
              className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
            />
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={submitting || !text.trim()}
              className="rounded-lg bg-pme-blue px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            >
              Post
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attachment display
// ---------------------------------------------------------------------------

function AttachmentGallery({
  urls,
  names,
  types,
}: {
  urls: string[];
  names: string[];
  types: string[];
}) {
  const images = urls
    .map((url, i) => ({ url, name: names[i] ?? "file", type: types[i] ?? "" }))
    .filter((a) => isImageType(a.type));
  const docs = urls
    .map((url, i) => ({ url, name: names[i] ?? "file", type: types[i] ?? "" }))
    .filter((a) => !isImageType(a.type));

  return (
    <div className="mt-3 space-y-2">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((img, i) => (
            <a
              key={i}
              href={img.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <img
                src={img.url}
                alt={img.name}
                className="max-h-48 rounded-lg border border-gray-200 object-cover"
              />
            </a>
          ))}
        </div>
      )}
      {docs.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {docs.map((doc, i) => (
            <a
              key={i}
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
            >
              <Paperclip size={14} strokeWidth={1.75} aria-hidden /> {doc.name}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Post card
// ---------------------------------------------------------------------------

function PostCard({
  post,
  onReact,
}: {
  post: SocialPost;
  onReact: (postId: number, type: string) => void;
}) {
  const badgeStyle =
    POST_TYPE_STYLES[post.post_type] ?? "bg-gray-50 text-gray-700";
  const isSpecialPost = ["birthday", "anniversary", "welcome"].includes(
    post.post_type,
  );

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeStyle}`}
          >
            {formatPostType(post.post_type)}
          </span>
          {post.is_pinned && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Pin size={12} strokeWidth={1.75} aria-hidden /> Pinned</span>
          )}
        </div>
        <span className="text-xs text-gray-400">
          {relativeTime(post.published_at)}
        </span>
      </div>

      {post.author_name && (
        <p className="mt-2 text-sm font-semibold text-gray-900">
          {post.author_name}
        </p>
      )}

      {isSpecialPost && post.related_employee_name && (
        <p className="mt-2 text-lg font-bold text-gray-900">
          {post.related_employee_name}
        </p>
      )}

      {post.content && (
        <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">
          {post.content}
        </p>
      )}

      {post.values && post.values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {post.values.map((v, i) => (
            <span
              key={v.id ?? `custom-${i}`}
              className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700"
            >
              {v.name}
            </span>
          ))}
        </div>
      )}

      {post.attachment_urls &&
        post.attachment_urls.length > 0 && (
          <AttachmentGallery
            urls={post.attachment_urls}
            names={post.attachment_names ?? []}
            types={post.attachment_types ?? []}
          />
        )}

      <div className="mt-3 flex flex-wrap gap-2">
        {Object.entries(REACTION_ICONS).map(([type, icon]) => {
          const existing = post.reactions.find((r) => r.type === type);
          const count = existing?.count ?? 0;
          const reacted = existing?.reacted ?? false;
          if (count === 0 && !["clap", "heart", "fire", "star", "thumbsup"].includes(type)) return null;
          return (
            <button
              key={type}
              type="button"
              onClick={() => onReact(post.id, type)}
              className={[
                "flex items-center gap-1 rounded-full px-3 py-1 text-sm transition-colors",
                reacted
                  ? "bg-blue-50 ring-1 ring-blue-200"
                  : "bg-gray-50 hover:bg-gray-100",
              ].join(" ")}
            >
              <span>{icon}</span>
              {count > 0 && (
                <span className="text-xs text-gray-600">{count}</span>
              )}
            </button>
          );
        })}
        <EmojiPickerButton onSelect={(type) => onReact(post.id, type)} />
      </div>

      <div className="mt-3 border-t border-gray-100 pt-3">
        <CommentsSection
          postId={post.id}
          commentCount={post.comment_count}
          recognitionId={post.recognition_id}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Emoji picker (for adding less common reactions)
// ---------------------------------------------------------------------------

function EmojiPickerButton({
  onSelect,
}: {
  onSelect: (type: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 rounded-full bg-gray-50 px-3 py-1 text-sm text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        title="More reactions"
      >
        +
      </button>
      {open && (
        <div className="absolute bottom-full right-0 z-20 mb-1 flex flex-wrap gap-0.5 rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg">
          {Object.entries(REACTION_ICONS).map(([type, icon]) => (
            <button
              key={type}
              type="button"
              onClick={() => {
                onSelect(type);
                setOpen(false);
              }}
              className="rounded p-1.5 text-lg hover:bg-gray-100"
              title={REACTION_LABELS[type] ?? type}
                aria-label={REACTION_LABELS[type] ?? type}
            >
              {icon}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Give recognition modal
// ---------------------------------------------------------------------------

interface UploadedAttachment {
  url: string;
  name: string;
  content_type: string;
}

function GiveRecognitionModal({
  onClose,
}: {
  onClose: (created: boolean) => void;
}) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [message, setMessage] = useState("");
  const [values, setValues] = useState<CompanyValue[]>([]);
  const [selectedValues, setSelectedValues] = useState<number[]>([]);
  const [customValueText, setCustomValueText] = useState("");
  const [customValues, setCustomValues] = useState<
    { label: string; suggest: boolean }[]
  >([]);
  const [isPrivate, setIsPrivate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<CompanyValue[]>("/api/recognition/values");
        setValues(data.filter((v) => v.is_active));
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const toggleValue = (id: number) => {
    setSelectedValues((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );
  };

  const addCustomValue = (suggest: boolean) => {
    const label = customValueText.trim();
    if (!label) return;
    setCustomValues((prev) => [...prev, { label, suggest }]);
    setCustomValueText("");
  };

  const removeCustomValue = (idx: number) => {
    setCustomValues((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remaining = 5 - attachments.length;
    const toUpload = Array.from(files).slice(0, remaining);
    if (toUpload.length === 0) return;

    setUploading(true);
    for (const file of toUpload) {
      if (file.size > 10 * 1024 * 1024) continue;
      try {
        const formData = new FormData();
        formData.append("file", file);
        const resp = await fetch("/api/recognition/upload-attachment", {
          method: "POST",
          body: formData,
          credentials: "include",
        });
        if (resp.ok) {
          const data = (await resp.json()) as UploadedAttachment;
          setAttachments((prev) => [...prev, data]);
        }
      } catch {
        /* ignore */
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0 || !message.trim() || submitting) return;
    setSubmitting(true);
    try {
      await api.post("/api/recognition", {
        recipient_ids: selectedIds,
        message: message.trim(),
        value_ids: selectedValues,
        custom_values: customValues,
        is_private: isPrivate,
        attachment_urls: attachments.map((a) => a.url),
        attachment_names: attachments.map((a) => a.name),
        attachment_types: attachments.map((a) => a.content_type),
      });
      onClose(true);
    } catch {
      /* ignore */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => onClose(false)}
      />
      <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Give Recognition
          </h2>
          <button
            type="button"
            onClick={() => onClose(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={14} strokeWidth={2} aria-label="Remove" />
          </button>
        </div>

        {/* Recipients */}
        <label className="mb-1 block text-sm font-medium text-gray-700">
          Who would you like to recognise?
        </label>
        <div className="mb-4">
          <EmployeeSearchMulti
            values={selectedIds}
            onChange={setSelectedIds}
            placeholder="Search employees..."
          />
        </div>

        {/* Message */}
        <label className="mb-1 block text-sm font-medium text-gray-700">
          What would you like to say?
        </label>
        <div className="relative mb-4">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
            rows={4}
            maxLength={2000}
            placeholder="Share what they did and why it matters..."
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
          />
          <span className="absolute bottom-2 right-2 text-xs text-gray-400">
            {message.length}/2000
          </span>
        </div>

        {/* Values */}
        {values.length > 0 && (
          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Which values did they exemplify?
            </label>
            <div className="flex flex-wrap gap-2">
              {values.map((v) => (
                <label
                  key={v.id}
                  className={[
                    "cursor-pointer rounded-full px-3 py-1 text-xs font-medium transition-colors",
                    selectedValues.includes(v.id)
                      ? "bg-indigo-100 text-indigo-800 ring-1 ring-indigo-300"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                  ].join(" ")}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={selectedValues.includes(v.id)}
                    onChange={() => toggleValue(v.id)}
                  />
                  {v.name}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Custom values */}
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Custom value tag
          </label>
          {customValues.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {customValues.map((cv, i) => (
                <span
                  key={i}
                  className={[
                    "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
                    cv.suggest
                      ? "bg-amber-50 text-amber-700"
                      : "bg-indigo-50 text-indigo-700",
                  ].join(" ")}
                >
                  {cv.label}
                  {cv.suggest && (
                    <span className="text-[10px] text-amber-500">
                      (suggested)
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeCustomValue(i)}
                    className="ml-0.5 hover:text-red-500"
                  >
                    <X size={14} strokeWidth={2} aria-label="Remove" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={customValueText}
              onChange={(e) => setCustomValueText(e.target.value.slice(0, 64))}
              placeholder="e.g. Quick Learner"
              className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
            />
            <button
              type="button"
              onClick={() => addCustomValue(false)}
              disabled={!customValueText.trim()}
              className="whitespace-nowrap rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              title="Use once on this recognition only"
            >
              One-time
            </button>
            <button
              type="button"
              onClick={() => addCustomValue(true)}
              disabled={!customValueText.trim()}
              className="whitespace-nowrap rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-40"
              title="Suggest as a permanent company value (requires admin approval)"
            >
              Suggest
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-400">
            One-time tags appear on this post only. Suggested values are sent to
            HR for approval.
          </p>
        </div>

        {/* Attachments */}
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Attachments
          </label>
          {attachments.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {attachments.map((a, i) => (
                <div
                  key={i}
                  className="relative inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-gray-700"
                >
                  {a.content_type.startsWith("image/") ? (
                    <img
                      src={a.url}
                      alt={a.name}
                      className="h-10 w-10 rounded object-cover"
                    />
                  ) : (
                    <Paperclip size={14} strokeWidth={1.75} aria-hidden />
                  )}
                  <span className="max-w-[100px] truncate">{a.name}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(i)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X size={14} strokeWidth={2} aria-label="Remove" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {attachments.length < 5 && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                <Paperclip size={14} strokeWidth={1.75} aria-hidden /> Attach file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                className="hidden"
                onChange={(e) => void handleFileSelect(e.target.files)}
              />
              {uploading && (
                <span className="self-center text-xs text-gray-400">
                  Uploading...
                </span>
              )}
            </div>
          )}
          <p className="mt-1 text-xs text-gray-400">
            Max 5 files, 10 MB each. Images, PDF, Word, Excel.
          </p>
        </div>

        {/* Private toggle */}
        <label className="mb-5 flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={isPrivate}
            onChange={(e) => setIsPrivate(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-pme-blue focus:ring-pme-blue"
          />
          Private (only visible to recipient and admins)
        </label>

        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={submitting || selectedIds.length === 0 || !message.trim()}
          className="w-full rounded-lg bg-pme-blue py-2.5 text-sm font-semibold text-white transition-colors hover:bg-pme-blue/90 disabled:opacity-50"
        >
          {submitting ? "Posting..." : "Post"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const FEED_FILTERS = { filter: "all" };

export default function SocialFeedPage() {
  const [filters, setFilterParam] = useFilterParams(FEED_FILTERS);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { isEnabled } = useFeatures();

  const loadFeed = useCallback(
    async (p: number, reset: boolean) => {
      setLoading(true);
      try {
        const data = await api.get<FeedResponse>(
          `/api/social/feed?page=${p}&per_page=20&type=${filters.filter}`,
        );
        setPosts((prev) => (reset ? data.items : [...prev, ...data.items]));
        setHasMore(data.page * data.per_page < data.total);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    },
    [filters.filter],
  );

  useEffect(() => {
    setPage(1);
    void loadFeed(1, true);
  }, [loadFeed]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    void loadFeed(next, false);
  };

  const handleReact = async (postId: number, reactionType: string) => {
    try {
      const resp = await api.post<{ action: string; reaction_type: string; old_reaction_type?: string }>(
        `/api/social/${postId}/react`,
        { reaction_type: reactionType },
      );
      setPosts((prev) =>
        prev.map((p) => {
          if (p.id !== postId) return p;

          if (resp.action === "removed") {
            return {
              ...p,
              reactions: p.reactions
                .map((r) =>
                  r.type === reactionType
                    ? { ...r, count: r.count - 1, reacted: false }
                    : r,
                )
                .filter((r) => r.count > 0),
            };
          }

          if (resp.action === "replaced" && resp.old_reaction_type) {
            let updated = p.reactions
              .map((r) => {
                if (r.type === resp.old_reaction_type)
                  return { ...r, count: r.count - 1, reacted: false };
                if (r.type === reactionType)
                  return { ...r, count: r.count + 1, reacted: true };
                return r;
              })
              .filter((r) => r.count > 0);
            if (!updated.some((r) => r.type === reactionType)) {
              updated = [...updated, { type: reactionType, count: 1, reacted: true }];
            }
            return { ...p, reactions: updated };
          }

          // added
          const existing = p.reactions.find((r) => r.type === reactionType);
          if (existing) {
            return {
              ...p,
              reactions: p.reactions.map((r) =>
                r.type === reactionType
                  ? { ...r, count: r.count + 1, reacted: true }
                  : r,
              ),
            };
          }
          return {
            ...p,
            reactions: [
              ...p.reactions,
              { type: reactionType, count: 1, reacted: true },
            ],
          };
        }),
      );
    } catch {
      /* ignore */
    }
  };

  const handleModalClose = (created: boolean) => {
    setShowModal(false);
    if (created) {
      setPage(1);
      void loadFeed(1, true);
    }
  };

  const recognitionOn = isEnabled("recognition_enabled");

  const filteredPosts = useMemo(
    () =>
      posts.filter((p) => {
        if (p.post_type === "recognition" && !recognitionOn) return false;
        if (p.post_type === "birthday" && !isEnabled("birthday_celebrations_enabled")) return false;
        if (p.post_type === "anniversary" && !isEnabled("anniversary_celebrations_enabled")) return false;
        return true;
      }),
    [posts, isEnabled, recognitionOn],
  );

  const visibleFilters = useMemo(
    () => FILTERS.filter((f) => f.key !== "recognition" || recognitionOn),
    [recognitionOn],
  );

  return (
    <div className="flex min-h-dvh flex-col safe-x">
      <header className="safe-top bg-white px-5 pt-4 pb-3 shadow-sm">
        <div className="flex items-start justify-between pr-28">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Social Feed
            </h1>
            <p className="text-sm text-gray-500">
              Stay connected with your team
            </p>
          </div>
          {recognitionOn && (
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-pme-blue/90"
            >
              Give Recognition
            </button>
          )}
        </div>

        <div className="mt-3 flex gap-1 overflow-x-auto">
          {visibleFilters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilterParam("filter", f.key)}
              className={[
                "whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                filters.filter === f.key
                  ? "bg-pme-blue text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200",
              ].join(" ")}
            >
              {f.label}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-5 pb-24 pt-4">
        <div className="space-y-4">
          {loading && posts.length === 0 && (
            <p className="py-12 text-center text-sm text-gray-400">
              Loading...
            </p>
          )}
          {!loading && filteredPosts.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-lg text-gray-400">No posts yet</p>
              <p className="mt-1 text-sm text-gray-400">
                Check back later for updates from your team.
              </p>
            </div>
          )}
          {filteredPosts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onReact={(id, type) => void handleReact(id, type)}
            />
          ))}
          {hasMore && !loading && (
            <div className="py-4 text-center">
              <button
                type="button"
                onClick={handleLoadMore}
                className="rounded-lg border border-gray-200 bg-white px-6 py-2 text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-50"
              >
                Load more
              </button>
            </div>
          )}
        </div>
      </main>

      {showModal && recognitionOn && <GiveRecognitionModal onClose={handleModalClose} />}

      <BottomNav />
    </div>
  );
}
